import * as i0 from '@angular/core';
import { InjectionToken, EventEmitter, LOCALE_ID, Component, ChangeDetectionStrategy, Inject, Input, Output, ViewChild, NgModule } from '@angular/core';
import * as i4 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i3 from 'primeng/rating';
import { RatingModule } from 'primeng/rating';
import * as i6 from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { DataViewPagingComponent } from '@arematics/core/dataview';
import * as i1 from '@arematics/core/api';
import { LocalizedDatePipeModule, PipesModule } from '@arematics/core/api';
import * as i5 from 'primeng/toolbar';
import { ToolbarModule } from 'primeng/toolbar';
import { IconComponent } from '@arematics/core/icon';
import { ButtonComponent } from '@arematics/core/button';
import { OwnerComponent } from '@arematics/core/owner';
import { UserVotingComponent } from '@arematics/core/user-voting';
import * as i2 from '@arematics/keycloak-angular';

const FEEDBACK_SERVICE = new InjectionToken('feedback.service');

class FeedbackComponent {
    name;
    auth;
    service;
    ref;
    locale;
    feedback;
    vote;
    edit = false;
    editEmitter = new EventEmitter();
    deleteEmitter = new EventEmitter();
    upvotedChange = new EventEmitter();
    regionName;
    url;
    refresh = (vote) => {
        this.vote = vote;
        this.ref.markForCheck();
    };
    localeParser;
    constructor(name, auth, service, ref, locale) {
        this.name = name;
        this.auth = auth;
        this.service = service;
        this.ref = ref;
        this.locale = locale;
        this.localeParser = new Intl.DisplayNames(locale, { type: 'region' });
    }
    ngOnInit() {
        this.service.getFeedbackVote(this.feedback.id).subscribe(this.refresh);
        this.regionName = this.localeParser.of(this.feedback.region);
    }
    updateFeedback(vote) {
        if (this.vote) {
            if (vote === undefined) {
                this.service.deleteFeedbackVote(this.vote).subscribe(() => {
                    this.vote = undefined;
                    this.ref.markForCheck();
                });
            }
            else {
                this.vote.upvote = vote.upvote;
                this.service.updateFeedbackVote(this.vote).subscribe(this.refresh);
            }
        }
        else if (vote) {
            const owner = this.auth.getKeycloakInstance().profile?.id;
            if (owner) {
                const item = {
                    owner,
                    feedbackId: this.feedback.id,
                    upvote: vote.upvote
                };
                this.service.updateFeedbackVote(item).subscribe(this.refresh);
            }
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: FeedbackComponent, deps: [{ token: i1.NameService }, { token: i2.KeycloakService }, { token: FEEDBACK_SERVICE }, { token: i0.ChangeDetectorRef }, { token: LOCALE_ID }], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: FeedbackComponent, isStandalone: true, selector: "a-feedback[feedback]", inputs: { feedback: "feedback", edit: "edit" }, outputs: { editEmitter: "editEmitter", deleteEmitter: "deleteEmitter", upvotedChange: "upvotedChange" }, ngImport: i0, template: `
		<div class="p-2 mt-2 mb-2">
			<a-owner [uuid]="feedback.publisher"></a-owner>
			<div class="pt-1 flex justify-content-between flex-wrap">
				<div>
					<div class="flex flex-row align-items-center">
						<p-rating
							[ngModel]="feedback.stars"
							[disabled]="true"
							[cancel]="false"
							[readonly]="true"></p-rating>
						<div class="ml-2 font-bold">{{ feedback.title }}</div>
					</div>
					<div class="text-sm opacity-70" *ngIf="regionName" i18n="@@feedback.from">
						From {{ regionName }} on {{ feedback.createdAt | localizedDate }}
					</div>
				</div>
				<div *ngIf="!edit">
					<a-user-voting
						[vote]="vote"
						[upvotes]="feedback.upvotes"
						(voteChange)="updateFeedback($event)"></a-user-voting>
				</div>
			</div>
			<p [innerHTML]="feedback.feedbackText | safe : 'html'"></p>
			<p-toolbar>
				<div class="p-toolbar-group-right" *ngIf="edit">
					<a-button
						[text]="true"
						i18n-label="@@edit"
						label="Edit"
						(clicked)="editEmitter.emit(feedback)"></a-button>
					<a-icon icon="trash" (click)="deleteEmitter.emit(feedback)"></a-icon>
				</div>
			</p-toolbar>
		</div>
	`, isInline: true, dependencies: [{ kind: "component", type: OwnerComponent, selector: "a-owner[uuid]", inputs: ["size", "loggedInUser", "uuid"] }, { kind: "ngmodule", type: RatingModule }, { kind: "component", type: i3.Rating, selector: "p-rating", inputs: ["disabled", "readonly", "stars", "cancel", "iconOnClass", "iconOnStyle", "iconOffClass", "iconOffStyle", "iconCancelClass", "iconCancelStyle"], outputs: ["onRate", "onCancel"] }, { kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i4.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "ngmodule", type: LocalizedDatePipeModule }, { kind: "pipe", type: i1.LocalizedDatePipe, name: "localizedDate" }, { kind: "ngmodule", type: PipesModule }, { kind: "pipe", type: i1.SafePipe, name: "safe" }, { kind: "ngmodule", type: ToolbarModule }, { kind: "component", type: i5.Toolbar, selector: "p-toolbar", inputs: ["style", "styleClass", "ariaLabelledBy"] }, { kind: "component", type: ButtonComponent, selector: "a-button", inputs: ["label", "icon", "iconPos", "class", "fit", "disabled", "loading", "severity", "text", "rounded", "raised", "outlined"], outputs: ["clicked"] }, { kind: "component", type: IconComponent, selector: "a-icon[icon]", inputs: ["icon", "size", "solid", "class"] }, { kind: "ngmodule", type: FormsModule }, { kind: "directive", type: i6.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i6.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: UserVotingComponent, selector: "a-user-voting", inputs: ["upvotes", "vote"], outputs: ["voteChange"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: FeedbackComponent, decorators: [{
            type: Component,
            args: [{ selector: 'a-feedback[feedback]', standalone: true, template: `
		<div class="p-2 mt-2 mb-2">
			<a-owner [uuid]="feedback.publisher"></a-owner>
			<div class="pt-1 flex justify-content-between flex-wrap">
				<div>
					<div class="flex flex-row align-items-center">
						<p-rating
							[ngModel]="feedback.stars"
							[disabled]="true"
							[cancel]="false"
							[readonly]="true"></p-rating>
						<div class="ml-2 font-bold">{{ feedback.title }}</div>
					</div>
					<div class="text-sm opacity-70" *ngIf="regionName" i18n="@@feedback.from">
						From {{ regionName }} on {{ feedback.createdAt | localizedDate }}
					</div>
				</div>
				<div *ngIf="!edit">
					<a-user-voting
						[vote]="vote"
						[upvotes]="feedback.upvotes"
						(voteChange)="updateFeedback($event)"></a-user-voting>
				</div>
			</div>
			<p [innerHTML]="feedback.feedbackText | safe : 'html'"></p>
			<p-toolbar>
				<div class="p-toolbar-group-right" *ngIf="edit">
					<a-button
						[text]="true"
						i18n-label="@@edit"
						label="Edit"
						(clicked)="editEmitter.emit(feedback)"></a-button>
					<a-icon icon="trash" (click)="deleteEmitter.emit(feedback)"></a-icon>
				</div>
			</p-toolbar>
		</div>
	`, changeDetection: ChangeDetectionStrategy.OnPush, imports: [
                        OwnerComponent,
                        RatingModule,
                        CommonModule,
                        LocalizedDatePipeModule,
                        PipesModule,
                        ToolbarModule,
                        ButtonComponent,
                        IconComponent,
                        FormsModule,
                        LocalizedDatePipeModule,
                        UserVotingComponent
                    ] }]
        }], ctorParameters: function () { return [{ type: i1.NameService }, { type: i2.KeycloakService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [FEEDBACK_SERVICE]
                }] }, { type: i0.ChangeDetectorRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [LOCALE_ID]
                }] }]; }, propDecorators: { feedback: [{
                type: Input
            }], edit: [{
                type: Input
            }], editEmitter: [{
                type: Output
            }], deleteEmitter: [{
                type: Output
            }], upvotedChange: [{
                type: Output
            }] } });

class FeedbackListComponent {
    view;
    query;
    rowsPerPageOptions = [4, 8, 12];
    editEmitter = new EventEmitter();
    deleteEmitter = new EventEmitter();
    editable = false;
    constructor() { }
    refresh() {
        this.view.refresh();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: FeedbackListComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: FeedbackListComponent, selector: "a-feedback-list[query]", inputs: { query: "query", rowsPerPageOptions: "rowsPerPageOptions", editable: "editable" }, outputs: { editEmitter: "editEmitter", deleteEmitter: "deleteEmitter" }, viewQueries: [{ propertyName: "view", first: true, predicate: ["view"], descendants: true }], ngImport: i0, template: `
		<a-dataview
			#view
			[query]="query"
			[rowsPerPageOptions]="rowsPerPageOptions"
			defaultLayout="list"
			[optionsEnabled]="false">
			<ng-content></ng-content>
			<ng-template #listItem let-item>
				<a-feedback
					[feedback]="item"
					[edit]="editable"
					(editEmitter)="editEmitter.emit($event)"
					(deleteEmitter)="deleteEmitter.emit($event)">
				</a-feedback>
			</ng-template>
		</a-dataview>
	`, isInline: true, dependencies: [{ kind: "component", type: i0.forwardRef(function () { return FeedbackComponent; }), selector: "a-feedback[feedback]", inputs: ["feedback", "edit"], outputs: ["editEmitter", "deleteEmitter", "upvotedChange"] }, { kind: "component", type: i0.forwardRef(function () { return DataViewPagingComponent; }), selector: "a-dataview[query]", inputs: ["query", "mapper", "rowsPerPageOptions", "defaultLayout", "optionsEnabled", "options", "column"], outputs: ["optionsChange"] }] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: FeedbackListComponent, decorators: [{
            type: Component,
            args: [{ selector: 'a-feedback-list[query]', template: `
		<a-dataview
			#view
			[query]="query"
			[rowsPerPageOptions]="rowsPerPageOptions"
			defaultLayout="list"
			[optionsEnabled]="false">
			<ng-content></ng-content>
			<ng-template #listItem let-item>
				<a-feedback
					[feedback]="item"
					[edit]="editable"
					(editEmitter)="editEmitter.emit($event)"
					(deleteEmitter)="deleteEmitter.emit($event)">
				</a-feedback>
			</ng-template>
		</a-dataview>
	` }]
        }], ctorParameters: function () { return []; }, propDecorators: { view: [{
                type: ViewChild,
                args: ['view']
            }], query: [{
                type: Input
            }], rowsPerPageOptions: [{
                type: Input
            }], editEmitter: [{
                type: Output
            }], deleteEmitter: [{
                type: Output
            }], editable: [{
                type: Input
            }] } });
class FeedbackListModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: FeedbackListModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: FeedbackListModule, declarations: [FeedbackListComponent], imports: [CommonModule, RatingModule, FormsModule, PipesModule, FeedbackComponent, DataViewPagingComponent], exports: [FeedbackListComponent] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: FeedbackListModule, imports: [CommonModule, RatingModule, FormsModule, PipesModule, FeedbackComponent, DataViewPagingComponent] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: FeedbackListModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [FeedbackListComponent],
                    exports: [FeedbackListComponent],
                    imports: [CommonModule, RatingModule, FormsModule, PipesModule, FeedbackComponent, DataViewPagingComponent]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { FEEDBACK_SERVICE, FeedbackComponent, FeedbackListComponent, FeedbackListModule };
